import type { EmberColorPalette } from '@athrelaris/shared';
import {
  Link,
  Outlet,
  createRootRoute,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANDING_PALETTES } from '../data/landing-palettes';
import { type AppLocale, setUserLocale } from '../i18n/index.js';
import { getAdminMe } from '../lib/api';
import { performSignOut } from '../lib/auth-actions';
import { useSession } from '../lib/auth-client';
import { useEmberTheme } from '../lib/use-ember-theme';

// Phase A (landing-only) ships with no productive API, so /palettes/sample
// is unreachable. We still want the page to wear a real Ember's tones —
// the landing already shows three Embers (earth / fire / air) inline,
// so picking one of their pre-extracted palettes at random per visit
// gives the surface variety without a network call. Pre-extracted by
// apps/api/scripts/extract-landing-palettes.ts.
function pickLandingPalette(): EmberColorPalette {
  const idx = Math.floor(Math.random() * LANDING_PALETTES.length);
  // The list is non-empty (asserted by the build script), so the
  // ?? branch never fires; the cast keeps TS quiet without a
  // non-null assertion.
  return (LANDING_PALETTES[idx] ?? LANDING_PALETTES[0])!.palette;
}

export const Route = createRootRoute({
  component: RootLayout,
});

interface Pulse {
  id: number;
  x: number;
  y: number;
}

const PULSE_DURATION_MS = 1600;

function RootLayout() {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useSession();
  const navigate = useNavigate();
  const currentLocale: AppLocale = i18n.language?.startsWith('es') ? 'es' : 'en';
  // During /awakening the user has a session but no bond yet; the
  // global Letters / Memories links would lead to routes that bounce
  // back here. Hide the in-app nav until the bond exists. /admin too —
  // an awakening visitor isn't an operator yet.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isPreBondLoggedIn = pathname.startsWith('/awakening');
  const logoRef = useRef<HTMLButtonElement | null>(null);
  const [pulses, setPulses] = useState<Pulse[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close the mobile menu whenever the route changes — covers Link
  // clicks (which also clear the state inline) AND back/forward nav.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  // Esc closes the menu. Standard modal expectation.
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  // Lock background scroll while the mobile menu is open. The overlay
  // already covers the whole viewport, but without this the page can
  // still scroll under the backdrop on iOS Safari which feels broken.
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  // Pre-Bond surfaces (login / verify / awakening / index) re-tint each
  // visit with a random palette borrowed from some real Ember in the
  // world, instead of staying on the brand default. When the user is
  // logged in, bond pages apply their own palette and this stays null
  // so the Ember-specific theme wins. Same applies after logout: the
  // sample re-fetches and the CSS vars revert to a fresh anonymous
  // theme rather than lingering on the previous bond's colors.
  const [samplePalette, setSamplePalette] = useState<EmberColorPalette | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const hasSession = Boolean(data?.session);

  // Probe the admin gateway once per logged-in session so we can
  // surface the Admin link conditionally. 403 = silently treat as
  // non-admin (the API already returns ok: false on a thrown error
  // via getAdminMe).
  useEffect(() => {
    if (isPending) return;
    if (!hasSession) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const me = await getAdminMe();
      if (!cancelled) setIsAdmin(Boolean(me?.isAdmin));
    })();
    return () => {
      cancelled = true;
    };
  }, [isPending, hasSession]);
  useEffect(() => {
    if (isPending) return undefined;
    if (hasSession) {
      setSamplePalette(null);
      return undefined;
    }
    // No session, no bond — source a sample palette from the static
    // landing palettes shipped with the bundle. Pick once per mount so
    // navigating between landing → /coming-soon → /privacy keeps the
    // same tint instead of flickering on each route. Same path applies
    // to pre-session visits on app.athrelaris.com (login surface) —
    // they still get a tinted shell instead of brand defaults.
    setSamplePalette(pickLandingPalette());
    return undefined;
  }, [isPending, hasSession]);
  // Three states of session resolution we have to respect:
  // - isPending: we don't yet know if there's a session. Hand the
  //   hook `undefined` so it doesn't touch :root — the cache that
  //   module-load hydrated stays applied. This was the regression on
  //   refresh: the initial `samplePalette = null` was running
  //   applyPalette(null) before the session even resolved, wiping the
  //   bond palette that had just been restored.
  // - hasSession resolved true: bond surfaces own the paint via the
  //   persistent cache; root stays out of the way.
  // - hasSession resolved false: render the random Ember sample,
  //   ephemeral so it doesn't write to cache.
  const rootPalette = isPending ? undefined : hasSession ? undefined : samplePalette;
  useEmberTheme(rootPalette, { persistent: false });

  async function handleSignOut() {
    await performSignOut();
    await navigate({ to: '/login' });
  }

  function fireLogoPulse() {
    const el = logoRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const id = Date.now() + Math.random();
    setPulses((prev) => [...prev, { id, x, y }]);
    window.setTimeout(() => {
      setPulses((prev) => prev.filter((p) => p.id !== id));
    }, PULSE_DURATION_MS);
  }

  // The logo doubles as the universal "return home" doorway. We fire
  // the ripple first (visual feedback that the click landed) and let
  // the navigation happen alongside it — TanStack handles the route
  // change synchronously, the ripple plays out fixed-position over
  // whatever surface mounts next.
  //
  // Routing rules:
  //   - Logged-in user → /home (their bond)
  //   - No session → / (the public landing on athrelaris.com, OR a
  //     redirect to /login on app.athrelaris.com because the `/`
  //     route is hostname-aware).
  // One handler, two hostnames, no isLandingOnly check needed.
  function handleLogoClick() {
    fireLogoPulse();
    void navigate({ to: data?.session ? '/home' : '/' });
  }

  return (
    <div className="mx-auto min-h-screen max-w-md px-6 pt-10 pb-16 sm:max-w-2xl sm:px-10 sm:pt-14 lg:max-w-6xl lg:px-12 lg:pt-16">
      {/* Brand mark — visible on every page so the surface always carries
       * Athrelaris identity. Click triggers a page-wide ripple wave that
       * emanates from the logo center; resolved via getBoundingClientRect
       * at click time so position is correct across all breakpoints
       * without responsive class plumbing. */}
      <div className="absolute top-4 left-4 z-50 sm:top-6 sm:left-6 lg:top-8 lg:left-10">
        <button
          ref={logoRef}
          type="button"
          onClick={handleLogoClick}
          aria-label={
            data?.session ? t('common.header.logoLabelLoggedIn') : t('common.header.logoLabel')
          }
          className="block cursor-pointer"
        >
          <img
            src="/logo.png"
            alt=""
            width={56}
            height={56}
            className={`select-none transition-all duration-500 ${
              pulses.length > 0
                ? 'opacity-100 logo-pulse-active'
                : 'opacity-60 hover:opacity-90'
            }`}
            draggable={false}
          />
        </button>
      </div>

      {/* Page-wide ripple host. Each pulse is fixed-positioned at the
       * logo center captured at click time, sized to 100vmax × 100vmax so
       * a single scale animation (0 → 2.5) covers the viewport diagonal
       * regardless of aspect ratio. Pointer-events: none so the wave
       * passes through clickable content beneath. */}
      {pulses.map((pulse) => (
        <span
          key={pulse.id}
          aria-hidden="true"
          className="pointer-events-none fixed z-40 h-[100vmax] w-[100vmax] animate-pageRipple rounded-full border-[3px] border-accent-strong/80"
          style={{ top: pulse.y, left: pulse.x }}
        />
      ))}

      {/* Pre-bond locale toggle. Visible only while there is no
       * session — once the bond is loaded, the surface follows
       * bond.locale and a manual toggle would lie about what gets
       * persisted. The current locale is rendered slightly brighter
       * so the inactive option reads as the alternative, not as
       * disabled. */}
      {!data?.session && !isPending && (
        <div className="absolute top-4 right-4 z-50 flex items-center gap-2 sm:top-6 sm:right-6 lg:top-8 lg:right-10">
          <button
            type="button"
            onClick={() => setUserLocale('en')}
            aria-pressed={currentLocale === 'en'}
            className={`font-display text-[0.62rem] uppercase tracking-[0.32em] transition sm:text-[0.68rem] sm:tracking-[0.36em] ${
              currentLocale === 'en'
                ? 'text-ash-100'
                : 'text-ash-500 hover:text-ash-300'
            }`}
          >
            EN
          </button>
          <span aria-hidden="true" className="text-ash-700 text-xs">·</span>
          <button
            type="button"
            onClick={() => setUserLocale('es')}
            aria-pressed={currentLocale === 'es'}
            className={`font-display text-[0.62rem] uppercase tracking-[0.32em] transition sm:text-[0.68rem] sm:tracking-[0.36em] ${
              currentLocale === 'es'
                ? 'text-ash-100'
                : 'text-ash-500 hover:text-ash-300'
            }`}
          >
            ES
          </button>
        </div>
      )}

      {data?.session && (
        <header className="absolute top-4 right-4 z-50 flex items-center gap-4 sm:top-6 sm:right-6 sm:gap-5 lg:top-8 lg:right-10 lg:gap-7">
          {!isPreBondLoggedIn ? (
            <>
              {/* Desktop / tablet inline rail. On mobile (<640px) all
               * five nav items + the leave button overflow the row
               * and crash into the logo, so we collapse to a Menu
               * trigger below. */}
              <nav className="hidden items-center gap-5 sm:flex lg:gap-7">
                <Link
                  to="/inbox"
                  className="font-display text-ash-400 uppercase transition hover:text-ash-100 sm:text-[0.68rem] sm:tracking-[0.36em]"
                >
                  {t('common.header.letters')}
                </Link>
                <Link
                  to="/echoes"
                  className="font-display text-ash-400 uppercase transition hover:text-ash-100 sm:text-[0.68rem] sm:tracking-[0.36em]"
                >
                  {t('common.header.echoes')}
                </Link>
                <Link
                  to="/memories"
                  className="font-display text-ash-400 uppercase transition hover:text-ash-100 sm:text-[0.68rem] sm:tracking-[0.36em]"
                >
                  {t('common.header.memories')}
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="font-display text-ash-400 uppercase transition hover:text-ash-100 sm:text-[0.68rem] sm:tracking-[0.36em]"
                  >
                    {t('common.header.admin')}
                  </Link>
                )}
                <Link
                  to="/account"
                  className="font-display text-ash-400 uppercase transition hover:text-ash-100 sm:text-[0.68rem] sm:tracking-[0.36em]"
                >
                  {t('common.header.account')}
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="font-display text-ash-500 uppercase transition hover:text-ash-300 sm:text-[0.68rem] sm:tracking-[0.36em]"
                >
                  {t('common.header.leave')}
                </button>
              </nav>
              {/* Mobile-only trigger. Uses the same display register
               * as the inline rail so the surface stays consistent
               * across breakpoints. */}
              <button
                type="button"
                onClick={() => setIsMenuOpen(true)}
                aria-label={t('common.header.menuAriaOpen')}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                className="font-display text-ash-400 text-[0.62rem] uppercase tracking-[0.32em] transition hover:text-ash-100 sm:hidden"
              >
                {t('common.header.menu')}
              </button>
            </>
          ) : (
            // Pre-bond (during /awakening): only Leave, no full rail
            // to collapse, no mobile menu needed.
            <button
              type="button"
              onClick={handleSignOut}
              className="font-display text-ash-500 text-[0.62rem] uppercase tracking-[0.32em] transition hover:text-ash-300 sm:text-[0.68rem] sm:tracking-[0.36em]"
            >
              {t('common.header.leave')}
            </button>
          )}
        </header>
      )}

      {/* Mobile menu overlay. Mounted only when open so the closed
       * state contributes nothing to the DOM. Z-index sits above the
       * logo (z-50) and the page ripple host (z-40). Body scroll is
       * locked while open via the effect below. */}
      {isMenuOpen && data?.session && !isPreBondLoggedIn && (
        <div
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label={t('common.header.menuAriaOpen')}
          className="fixed inset-0 z-[60] sm:hidden"
        >
          <button
            type="button"
            aria-label={t('common.header.menuAriaClose')}
            onClick={() => setIsMenuOpen(false)}
            className="absolute inset-0 bg-paper-950/95 backdrop-blur-sm"
          />
          <div className="relative flex h-full flex-col items-center justify-center gap-8 px-8 animate-fadeUp">
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              aria-label={t('common.header.menuAriaClose')}
              className="absolute top-4 right-4 font-display text-ash-300 text-[0.62rem] uppercase tracking-[0.32em] transition hover:text-ash-100"
            >
              {t('common.header.close')}
            </button>
            <nav className="flex flex-col items-center gap-7">
              <Link
                to="/inbox"
                onClick={() => setIsMenuOpen(false)}
                className="font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:text-ash-50"
              >
                {t('common.header.letters')}
              </Link>
              <Link
                to="/echoes"
                onClick={() => setIsMenuOpen(false)}
                className="font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:text-ash-50"
              >
                {t('common.header.echoes')}
              </Link>
              <Link
                to="/memories"
                onClick={() => setIsMenuOpen(false)}
                className="font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:text-ash-50"
              >
                {t('common.header.memories')}
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsMenuOpen(false)}
                  className="font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:text-ash-50"
                >
                  {t('common.header.admin')}
                </Link>
              )}
              <Link
                to="/account"
                onClick={() => setIsMenuOpen(false)}
                className="font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:text-ash-50"
              >
                {t('common.header.account')}
              </Link>
            </nav>
            <div aria-hidden="true" className="h-px w-16 bg-ash-700" />
            <button
              type="button"
              onClick={async () => {
                setIsMenuOpen(false);
                await handleSignOut();
              }}
              className="font-display text-ash-400 text-[0.7rem] uppercase tracking-[0.36em] transition hover:text-ash-200"
            >
              {t('common.header.leave')}
            </button>
          </div>
        </div>
      )}
      <Outlet />
    </div>
  );
}
